(ns app.domain.party.model
  (:require [app.domain.business.model :as business-model]
            [app.domain.contractor-registration.model :as cr-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.requirement.model :as req-model]
            [app.domain.user.util :as user-util]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.child-entity :as child]
            [core.db.entity.util :as eu]
            [core.permission.resolver :as permission-resolver]
            [core.util.schemas :as schemas]
            [core.util.string :as str-util]
            [potpuri.core :as p]
            [schema.core :as s]))


(def default-invite-permissions
  #{:party/view-other-phases
    :party/add-application
    :party/modify-application})


(defn representee [party] (or (:party/user party) (:party/business party)))


(defn unchecked-user-party-email [party]
  (or (get party :party/email)
      (some-> (:party/user party) (get :user/email))))


(defn get-user-party-user-key [party k]
  (case k
    :user/email (unchecked-user-party-email party)
    (or (get party k)
        (some-> (:party/user party) (get k)))))


(defn get-user-key-no-ctx
  "[[get-user-key]] but only works when [[i18n-model/Ctx]] is not required, otherwise returns `nil`."
  [party k]
  (or (get-user-party-user-key party k)
      (when-some [business (:party/business party)]
        (business-model/get-user-key-no-ctx business k))))


(s/defn get-user-key [ctx :- i18n-model/Ctx, party k]
  (or (get-user-party-user-key party k)
      (when-some [business (:party/business party)]
        (business-model/get-user-key ctx business k))))

(s/defn get-user-key-for-user-or-business [ctx :- i18n-model/Ctx user-or-business k]
  (when user-or-business
    (case (:entity/type user-or-business)
      :entity.type/user
      (get user-or-business k)
      :entity.type/business
      (business-model/get-user-key ctx user-or-business k))))


(defn merge-user-and-party [party]
  (if-let [user (:party/user party)]
    ; These are Entities, cannot be directly used as Map
    (-> (select-keys user [:user/first-name :user/last-name :user/mobile-phone
                           :user/street-address :user/municipality :user/postal-code
                           :user/corporation-or-partnership :user/province
                           :user/unit-number :user/lot-con :user/other-phone
                           :user/integrations :user/licences])
        (merge party)
        (assoc :party/email (or (:party/email party) (:user/email user))
               :db/id (:db/id party)))
    party))

(defn merge-business-user-party-with-fake-keys
  "Provides compatibility with existing code by renaming business attributes to similar attributes in the
   user namespace, if the party is a business. If it is not, defers to merge-user-and-party.
   Be careful that business data is not accidentally presented as personal info in the wrong place.

   `user` can be nil, in which case first and last name will not be returned for a business party"
  [{:keys [lang langs tr]} {:party/keys [business] :as party} user]
  (if business
    (let [ctx           {:lang (or lang (first langs)), :tr tr}
          business-name (if lang
                          (business-model/format-name business lang)
                          (->> langs
                               (map #(business-model/format-name business %))
                               (str/join i18n-model/tr-concat-spacer)))
          subdivision   (business-model/format-subdivision business tr)]
      (-> (into {}
                (map (fn [k] [k (business-model/get-user-key ctx business k)]))
                [:user/street-address
                 :user/municipality
                 :user/postal-code
                 :user/mobile-phone])
          (assoc :user/corporation-or-partnership business-name
                 :user/province subdivision)
          (merge (select-keys user [:user/first-name :user/last-name]))))
    (merge-user-and-party party)))


(defn full-address-and-municipality [party]
  (let [address (user-util/full-address party)]
    (if-not (str/blank? address)
      address
      (if-some [business (:party/business party)]
        (business-model/full-address business)
        (if-some [user (:party/user party)]
          (user-util/full-address user)
          address)))))


(defn municipality-province [party tr]
  (or (user-util/municipality-province party)
      (some-> (:party/user party) user-util/municipality-province)
      (some-> (:party/business party) (business-model/municipality-province tr))))


(defn street-address+unit [party]
  (let [address (user-util/street-address+unit party)]
    (if-not (str/blank? address)
      address
      (if-let [business (:party/business party)]
        (:business/mailing-address business)
        (if-let [user (:party/user party)]
          (user-util/street-address+unit user)
          address)))))


(defn is-party? [party]
  (-> party :entity/type (= :entity.type/party)))


(defn- party-in-state-fn [state]
  (fn [party]
    (and (is-party? party)
         (= (:domain/state party) state))))


(def ^{:arglists '([party])} active?
  "Party state is 'active'. Does not quarantee that there is a user attached to
  the party, so do not rely on this for email sending."
  (party-in-state-fn :party.state/active))

(def ^{:arglists '([party])} pending?
  (party-in-state-fn :party.state/pending))

(def ^{:arglists '([party])} declined?
  (party-in-state-fn :party.state/declined))

(def removed? (du/in-state-fn :party.state/removed))

(defn notifiable? [party]
  (and
    ;; party still applicable - not removed, declined etc
    (active? party)
    ;; user has registered - only invite is sent before that
    (some? (representee party))
    ;; user has not disabled notifications
    (-> party :party/notifications-disabled not)))

(defn role-keys
  "Note: has time-travelling counterpart in `app.domain.party-role.time-travel/party-role-keys`"
  [party]
  (->> party :party/party-roles (map :party-role/key) set))

(defn has-role?
  "Note: has time-travelling counterpart in `app.domain.party-role.time-travel/party-has-role?`"
  [party role-key]
  (contains? (role-keys party) role-key))

(defn is-applicant? [party]
  (and (is-party? party)
       (has-role? party :role/applicant)))

(defn has-any-role?
  "Given a set of roles (as keywords), truthy when party has any of those"
  [role-keywords party]
  (->> (role-keys party)
       (set/intersection (set role-keywords))
       seq))

(defn integration-id [party group-id]
  (or (-> party
          :party/user
          :user/integrations
          (p/find-first {:user.integration/customer-id group-id})
          :user.integration/user-id)
      (-> party
          :user.integration/user-id)))

(defn get-email
  "Get the email of a user or manual party. Returns `nil` for a business party or if the party has an invalid email."
  [party]
  (when-some [email (unchecked-user-party-email party)]
    (when-not (schemas/check-email email)
      email)))

(defn get-emails
  "Get the email addresses to notify for this party - either manual input, email associated with user
  or the emails associated with business email recipient users"
  [party]
  (or (some-> (get-email party) vector)
      (some-> (:party/business party) business-model/get-emails)
      '()))

(defn user-party-full-name
  "Return party's full name"
  [party]
  (->> [:user/first-name :user/last-name]
       (map #(get-user-party-user-key party %))
       (remove str/blank?)
       (map str/trim)
       (str/join " ")))


(defn simple-name [party lang-or-langs]
  (if-some [business (:party/business party)]
    (business-model/business-name business lang-or-langs)
    (user-party-full-name party)))

(defn corporation-or-full-name [party lang-or-langs]
  (if-some [business (:party/business party)]
    (business-model/business-name business lang-or-langs)
    (let [{:user/keys [corporation-or-partnership] :as merged} (merge-user-and-party party)]
      (or (str-util/blank->nil corporation-or-partnership)
          (user-party-full-name merged)))))

(defn business-or-full-name [party lang]
  (let [business (:party/business party)]
    (if business
      (business-model/format-name business lang)
      (user-party-full-name party))))

(defn corporation-and-full-name-or-full-name
  "In case of pending party and missing name fall back to party email."
  [party lang-or-langs]
  (let [{:user/keys  [corporation-or-partnership]
         :party/keys [email]
         :as         merged}                      (merge-user-and-party party)
        fullname                                  (-> merged user-party-full-name str-util/blank->nil)]
    (if-some [business-name (or (str-util/blank->nil corporation-or-partnership)
                                (some-> (:party/business party)
                                        (business-model/format-name lang-or-langs)))]
      (str business-name
           (when fullname
             (str " (" fullname ")")))
      (str (or fullname email)))))

(defn party-forms-and-fields-apply? [workspace-child]
  ;; party forms and fields only apply to application phase
  (eu/is-type? :entity.type/application workspace-child))

(def ^{:arglists '([phase-entity])} phase-parties
  (child/child-entities-of-type-fn :entity.type/party))

(defn active-parties [phase-entity]
  (->> phase-entity
       phase-parties
       (filter active?)))

(defn active-parties-for-the-role [phase-entity role-key]
  (->> phase-entity
       active-parties
       (filter #(has-role? % role-key))))

(defn role-only-has-non-user-parties?
  "Returns true iff the role has active parties but none of them have user linked to them"
  [phase-entity role-key]
  (boolean
    (when-let [active-parties (some->> role-key (active-parties-for-the-role phase-entity))]
      (->> active-parties
           (filter (fn [party]
                     (or (:party/user party)
                         (and (:party/business party)
                              (or (:party/workspace-creator? party)
                                  ;; has accepted invites
                                  (and (->> party
                                            :invite/_party
                                            (filter #(-> % :invite/status (= :invite.status/accepted)))
                                            seq)
                                       (->> party
                                            :party/business
                                            business-model/business-users
                                            seq)))))))
           empty?))))

(defn find-same-party
  "Find a party from under `phase` which is either the source or copy
  of `party` (via copying parties to the next phase)."
  [phase party]
  (->> phase
       phase-parties
       (filter (fn [other-party]
                 (or (some-> party :party/copied-from (= other-party))
                     (some-> other-party :party/copied-from (= party))
                     (some-> other-party :party/copied-from (= (:party/copied-from party))))))
       first))

(defn- get-related-parties [party related-parties-fn]
  (->> party
       related-parties-fn
       rest ; Omit `party` itself.
       (take-while some?)))

(defn get-copied-from-parties [party]
  (get-related-parties party (partial iterate :party/copied-from)))

(defn get-copied-to-parties [party]
  (get-related-parties party (partial tree-seq #(-> (get % :party/_copied-from) empty? not) #(get % :party/_copied-from))))

(defn get-copied-parties
  "Find all parties in workspace that are related to `party` (transitively) either via :party/copied-from
  or :party/_copied-from."
  [party]
  (concat (get-copied-from-parties party) (get-copied-to-parties party)))

(defn latest-party-copy
  "Find the \"latest\" copied party based on this party."
  [party]
  (->> party
       (iterate (comp first :party/_copied-from))
       (take-while some?)
       last))

(defn phase-applicant-businesses [phase-entity]
  (->> phase-entity
       phase-parties
       (keep #(when (-> % role-keys (contains? :role/applicant))
                (:party/business %)))))

(defn user-in-party? [user {:party/keys [business] :as party}]
  ;; NOTE: user is probably of type cljs.core/PersistentHashMap here while :party/user is core.db.entity-api-mem/Entity
  ;; so the equality must be checked with :db/id, not directly
  (or (some-> party :party/user :db/id (= (:db/id user)))
      (and business
           (permission-resolver/permitted? business user :permission.business/act-on-behalf))))

(defn user-can-act-as-party? [user party]
  (and (active? party) (user-in-party? user party)))

(defn parties-for-user
  "Returns all parties for the `user` under the given `phase-entity`. If `role-id-or-kw` is provided,
   the returned parties must have that role.

   Note: has a time-travelling counterpart in `app.domain.party-role.time-travel/parties-for-user`"
  ([user phase-entity]
   (parties-for-user user phase-entity nil))
  ([user phase-entity role-id-or-kw]
   (->> phase-entity
        phase-parties
        (filter (fn [{:party/keys [party-roles] :as party}]
                  (and (user-can-act-as-party? user party)
                       (or (nil? role-id-or-kw)
                           (if (keyword? role-id-or-kw)
                             (has-role? party role-id-or-kw)
                             (some #(= (:db/id %) role-id-or-kw) party-roles)))))))))

(defn workspace-parties [workspace] (mapcat phase-parties (workspace-model/phase-entities workspace)))

(defn- generate-roles-string [lang roles]
  (->> roles
       (map #(role-model/party-role-name % lang))
       sort
       (str/join ", ")))

(defn join-roles-as-string
  "Generates comma-separated list from party's roles"
  [lang party]
  (->> (:party/party-roles party)
       (generate-roles-string lang)))

(defn join-roles-from-multiple-phases
  "Same as join-roles-as-string, but collects roles from multiple phases in case the same user has had different roles in different phases"
  [user phases lang]
  (->> phases
       (mapcat #(parties-for-user user %))
       (mapcat :party/party-roles)
       distinct
       (generate-roles-string lang)))

(defn full-name-with-roles
  "Returns a string with the party's full name and a list of their roles, e.g.:
  \"Antti Applikantti (Applicant, Payer)\""
  [lang party]
  (str (corporation-and-full-name-or-full-name party lang)
       (when-not (empty? (:party/party-roles party))
         (str " (" (join-roles-as-string lang party) ")"))))

(def snippet-variables ["name"
                        "address"
                        "street-address"
                        "city"
                        "state"
                        "postal-code"
                        "roles"
                        "business-type"
                        "business-number"])

(defn missing-roles
  ([phase]
   (missing-roles phase nil false))
  ([phase requirement-kinds]
   (missing-roles phase requirement-kinds false))
  ([phase requirement-kinds hide-deferred?]
   (let [present-roles?   (->> phase
                               active-parties
                               (mapcat role-keys)
                               set)
         applicable-role? (->> phase
                               :entity/parent
                               workspace-model/default-config-path
                               config-model/resolve-applicable-roles-for-target
                               (map :party-role/key)
                               set)
         missing-roles    (cond->> (req-model/phase-requirements phase)
                            true (filter #((set (or requirement-kinds [:requirement.kind/role])) (:requirement/kind %)))
                            hide-deferred? (filter #(-> % :requirement/deferred-by not))
                            true (map :requirement/party-role)
                            true (filter (fn [{role-key :party-role/key}]
                                           (and (not (present-roles? role-key))
                                                (applicable-role? role-key))))
                            true set)]
     missing-roles)))

(defn roles-with-unfulfilled-trade-requirement
  "Returns party roles that aren't assigned to parties who fulfill the trade requirement of the party role."
  [phase]
  (let [parties      (active-parties phase)
        requirements (req-model/phase-role-requirements phase)]
    (->> requirements
         (keep (fn [req]
                 (when-let [trade (some-> req :requirement/party-role :party-role/trade)]
                   (let [role                (:requirement/party-role req)
                         ;; trades found from parties with the current role
                         trades-from-parties (->> parties
                                                  (filter #(has-role? % (:party-role/key role)))
                                                  (mapcat #(some->> (cr-model/party-entity->registrations %)
                                                                    (mapcat cr-model/contractor-registration-trades)
                                                                    (map :registered-trade/work-type)))
                                                  (remove nil?)
                                                  set)]
                     (when (not (contains? trades-from-parties trade))
                       role))))))))

(defn phases-with-parties [workspace]
  (->> workspace
       workspace-model/phase-entities
       (remove (eu/is-type-fn :entity.type/archiving))))
